/**
 * The camera view's step guide and hint line, as ONE table from state to copy
 * (`observers-page-camera-stage`, FR3; with no mode since `observer-unified-draw-edit`, R6: it reads
 * what is picked, selected, open and waiting). Wording is Rob's v3 page (states 03, 04, 06, 07) and, where
 * v3 is silent, the v2 modal design it inherits. There is no *Save to the plan* step: the draft
 * saves itself.
 *
 * ⚠ Every hint must say what the drawing area actually does. A click on an EDGE adds a
 * corner (`PolygonPlotter2`'s `insertPoint`); a corner is removed through its own delete control.
 * So the edit hint keeps #1865's *Click an edge to add a corner* rather than v3 06's *Click a
 * corner to remove it* (plan, Checkpoint 1).
 */

import type { StepGuideModel } from '@repo/ui';

export interface StepGuideState {
  /** The picked entity's name, or `null` when nothing is picked (camera by camera). */
  pickedName: string | null;
  /** A region of the picked entity has been closed since it came on this camera. */
  pickedRegionClosed: boolean;
  /** Nothing picked: a region is closed and waiting for the entity it watches. */
  waiting: boolean;
  /** The selected observer's name, or `null` when none is selected. */
  selectedName: string | null;
  /** A region is being drawn (it has at least one point). */
  regionOpen: boolean;
  /** The picked entity has a region on this camera. */
  hasRegionsHere: boolean;
}

export const HINT_DRAW =
  'Click to add points · Click the first point to close the region · Ctrl+Z or Backspace removes the last point · Esc cancels';
export const hintLinked = (name: string): string =>
  `Pick the next entity in the list or the plan · Shift+click to add another region for ${name}`;
export const HINT_WAITING =
  'Pick an entity from the list to link this region · Esc discards the region';
export const HINT_CAMERA_IDLE =
  'Click a region to pick its entity · Shift+click to start a new region';
export const HINT_PICKED_NO_REGION =
  'Shift+click or Add Observer to start its region · Click a region to pick it';
export const HINT_EDIT_IDLE =
  'Click to edit an observer · Tab+click for the one underneath · Shift+click to draw a new one';
export const HINT_EDIT_PICKED =
  'Drag a corner to move it · Click an edge to add a corner · Tab+click for the one underneath · Esc deselects';

/**
 * First match wins: a region open; a region waiting; an observer selected; a region of the picked
 * entity closed here this visit and none selected (*Region linked*); the picked entity with no region
 * here; the picked entity with regions here and none selected; nothing picked. Closing a region
 * selects it (R3.4), so *Region linked* shows once the new observer has been deselected.
 */
export const stepGuideFor = (s: StepGuideState): StepGuideModel => {
  if (s.regionOpen) {
    return s.pickedName
      ? {
          labels: [`${s.pickedName} picked`, 'Draw its region'],
          current: 1,
          done: [0],
          hint: HINT_DRAW,
        }
      : {
          labels: ['Draw a region or pick an entity', 'Pick the entity it watches'],
          current: 0,
          done: [],
          hint: HINT_DRAW,
        };
  }
  if (s.waiting) {
    return {
      labels: ['Region drawn', 'Pick the entity it watches'],
      current: 1,
      done: [0],
      hint: HINT_WAITING,
    };
  }
  if (s.selectedName) {
    return {
      labels: [s.selectedName, 'Adjust it'],
      current: 1,
      done: [0],
      hint: HINT_EDIT_PICKED,
    };
  }
  if (s.pickedName) {
    if (s.pickedRegionClosed) {
      return {
        labels: [`${s.pickedName} picked`, 'Region linked'],
        current: null,
        done: [0, 1],
        hint: hintLinked(s.pickedName),
      };
    }
    if (!s.hasRegionsHere) {
      return {
        labels: [`${s.pickedName} picked`, 'Draw its region'],
        current: 1,
        done: [0],
        hint: HINT_PICKED_NO_REGION,
      };
    }
    return {
      labels: ['Pick an observer', 'Adjust it'],
      current: 0,
      done: [],
      hint: HINT_EDIT_IDLE,
    };
  }
  return {
    labels: ['Draw a region or pick an entity', 'Pick the entity it watches'],
    current: 0,
    done: [],
    hint: HINT_CAMERA_IDLE,
  };
};
